import React, { useEffect, useRef, useState } from 'react';
import { MapViewSite } from './types';
import MapViewIcon from './MapViewIcons';

interface MapViewSiteSelectorProps {
  sites: MapViewSite[];
  selectedSiteId: string | null;
  onSelectSite: (siteId: string) => void;
}

const MapViewSiteSelector: React.FC<MapViewSiteSelectorProps> = ({ sites, selectedSiteId, onSelectSite }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const selectedSite = sites.find((site) => site.id === selectedSiteId) || sites[0] || null;

  useEffect(() => {
    if (!open) return;

    const handleClick = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  if (!selectedSite) {
    return <div className="mv-site mv-site--empty">No site selected</div>;
  }

  return (
    <div className="mv-site" ref={wrapperRef}>
      <button
        type="button"
        className="mv-site__trigger"
        onClick={() => setOpen((current) => !current)}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={sites.length <= 1}
      >
        <span className="mv-site__pin" aria-hidden="true">
          <MapViewIcon name="site" size={17} />
        </span>
        <span className="mv-site__name">{selectedSite.name}</span>
        {sites.length > 1 ? (
          <span className={`mv-site__chevron ${open ? 'is-open' : ''}`} aria-hidden="true">
            <MapViewIcon name="chevron-down" size={15} />
          </span>
        ) : null}
      </button>

      {open && sites.length > 1 ? (
        <ul className="mv-site__menu" role="listbox" aria-label="Select site">
          {sites.map((site) => {
            const selected = site.id === selectedSite.id;
            return (
              <li key={site.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={`mv-site__option ${selected ? 'is-selected' : ''}`}
                  onClick={() => {
                    setOpen(false);
                    if (!selected) onSelectSite(site.id);
                  }}
                >
                  <span>{site.name}</span>
                  {selected ? (
                    <span className="mv-site__check" aria-hidden="true">
                      <MapViewIcon name="check" size={15} />
                    </span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
};

export default MapViewSiteSelector;
